import React, {ComponentProps, ComponentType, ReactNode, useCallback} from 'react';
import {useNavigate} from 'react-router-dom';
import {
  getProcessModalReasons,
  IVRetryModal,
  IVProcessModal,
  TranslationFn,
} from '@chekin/shared';
import {PATHS} from 'routes/constants';
import {IV_DOCUMENT_IMAGE_TYPES, STATUSES} from '@chekin/core';
import Button from '../../../components/common/Button';
import {ModalTwoButtonsWrapper} from '@guestapp/old-ui-kit';

type DefaultErrorModalProps = {
  message: string;
  description: string;
  children?: ReactNode;
};

type UseIVModalComponentsProps = {
  docType?: IV_DOCUMENT_IMAGE_TYPES;
  layout: ComponentProps<typeof IVProcessModal>['layoutMode'];
  required: ComponentProps<typeof IVProcessModal>['isRequired'];
  t: TranslationFn;
  errorMessage?: string;
  processState: STATUSES;
  isProcessModalOpen: boolean;
  closeAndCleanLoadingModal: () => void;
  isRetryModalOpen: boolean;
  handleRetryAgain: () => void;
  handleCancelRetryAgain: () => void;
  DefaultErrorModal: ComponentType<DefaultErrorModalProps>;
  closeErrorModal: () => void;
  continueRegistration: () => void;
};

function useIVModalComponents({
  docType,
  layout,
  required,
  t,
  errorMessage,
  processState,
  isProcessModalOpen,
  closeAndCleanLoadingModal,
  isRetryModalOpen,
  handleRetryAgain,
  handleCancelRetryAgain,
  DefaultErrorModal,
  closeErrorModal,
  continueRegistration,
}: UseIVModalComponentsProps) {
  const navigate = useNavigate();

  const ProcessModal = useCallback(() => {
    const reasons = getProcessModalReasons(t, docType);

    return (
      <IVProcessModal
        open={isProcessModalOpen}
        errorMessage={errorMessage}
        reasons={reasons}
        state={processState}
        onTryAgain={closeAndCleanLoadingModal}
        docType={docType}
        layoutMode={layout}
        isRequired={required}
        onVerifyLater={() => {
          navigate(`../../${PATHS.addPersonalDataForm}`);
        }}
      />
    );
  }, [
    closeAndCleanLoadingModal,
    docType,
    errorMessage,
    isProcessModalOpen,
    layout,
    processState,
    required,
    navigate,
    t,
  ]);

  const RetryModal = useCallback(() => {
    return (
      <IVRetryModal
        open={isRetryModalOpen}
        onClickTryAgain={handleRetryAgain}
        onCancel={handleCancelRetryAgain}
      />
    );
  }, [handleCancelRetryAgain, handleRetryAgain, isRetryModalOpen]);

  const ErrorModal = useCallback(() => {
    return (
      <DefaultErrorModal
        message={t('iv_error_message')}
        description={t('iv_error_description')}
      >
        <ModalTwoButtonsWrapper>
          <Button
            label={t('try_again')}
            onClick={() => {
              closeErrorModal();
            }}
          />
          <Button
            label={t('continue_the_registration')}
            onClick={continueRegistration}
            link
          />
        </ModalTwoButtonsWrapper>
      </DefaultErrorModal>
    );
  }, [DefaultErrorModal, closeErrorModal, continueRegistration, t]);

  return {ProcessModal, RetryModal, ErrorModal};
}

export {useIVModalComponents};
